/* All site copy lives here. British spelling. Lowercase quips are deliberate
 * brand voice. No em-dashes anywhere. All member/profile content is fictional
 * placeholder material until real community submissions replace it.
 */

export const APPLICATION_URL: string =
  import.meta.env.VITE_APPLICATION_URL || "#apply";

/* ------------------------------------------------------------------ */
/* Interaction 01 · WHAT BOAT ARE YOU IN?                              */
/* ------------------------------------------------------------------ */

export type Boat = {
  id: string;
  label: string;
  line: string;
  /** Response shown once selected. */
  reply: string;
};

export const boats: Boat[] = [
  {
    id: "fundraising",
    label: "Fundraising",
    line: "trying to convince someone your idea is worth betting on.",
    reply: "looks like you're in the FUNDRAISING boat.",
  },
  {
    id: "customers",
    label: "Customers",
    line: "you've built the thing. now where are the people?",
    reply: "looks like you're in the CUSTOMERS boat.",
  },
  {
    id: "sales",
    label: "Sales",
    line: "apparently 'just do outbound' wasn't enough.",
    reply: "looks like you're in the SALES boat.",
  },
  {
    id: "growth",
    label: "Growth",
    line: "more people should know about this. somehow.",
    reply: "looks like you're in the GROWTH boat.",
  },
  {
    id: "product",
    label: "Product",
    line: "the roadmap is a suggestion. shipping is the job.",
    reply: "looks like you're in the PRODUCT boat.",
  },
  {
    id: "hiring",
    label: "Hiring",
    line: "you need people. good people. preferably yesterday.",
    reply: "looks like you're in the HIRING boat.",
  },
  {
    id: "ai",
    label: "AI",
    line: "shipping faster. trusting harder.",
    reply: "looks like you're in the AI boat.",
  },
  {
    id: "launching",
    label: "Launching",
    line: "it's ready. well. ready enough.",
    reply: "looks like you're in the LAUNCHING boat.",
  },
  {
    id: "next",
    label: "What's next?",
    line: "things are working. now what?",
    reply: "looks like you're in the WHAT'S NEXT boat.",
  },
  {
    id: "broken",
    label: "Fixing something broken",
    line: "it worked on Friday.",
    reply: "looks like you're in the FIXING SOMETHING boat.",
  },
  {
    id: "else",
    label: "Something else",
    line: "wrong boat. great problem though.",
    reply: "looks like you're in a boat we haven't named yet.",
  },
];

export const boatPickerCopy = {
  title: "What boat are you in?",
  sub: "Everyone's trying to get somewhere. What's the thing you're trying to move forward right now?",
  afterReply: "there are probably a few other people in here too.",
  ctaQuestion: "want to find your people?",
  ctaLabel: "Apply to Same Boat",
  fineprint:
    "These are examples, not fixed programmes. Your boat can change. That's sort of the point.",
};

/* ------------------------------------------------------------------ */
/* Interaction 02 · THE BOAT MAP (night harbour)                       */
/* ------------------------------------------------------------------ */

export type MapBoat = {
  id: string;
  name: string;
  blurb: string;
  /** Position on the chart, percentage coordinates. */
  x: number;
  y: number;
};

export const mapBoats: MapBoat[] = [
  { id: "raise", name: "THE RAISE", blurb: "for people convincing strangers to bet on them.", x: 16, y: 30 },
  { id: "first10", name: "THE FIRST 10", blurb: "for people trying to find their first real customers.", x: 38, y: 18 },
  { id: "pipeline", name: "THE PIPELINE", blurb: "for people turning conversations into revenue.", x: 63, y: 26 },
  { id: "build", name: "THE BUILD", blurb: "for people shipping the thing. daily.", x: 84, y: 36 },
  { id: "crew", name: "THE CREW", blurb: "for people hiring their first real team.", x: 24, y: 62 },
  { id: "launch", name: "THE LAUNCH", blurb: "for people counting down to something public.", x: 47, y: 52 },
  { id: "pivot", name: "THE PIVOT", blurb: "for people steering somewhere new.", x: 70, y: 66 },
  { id: "unknown", name: "THE UNKNOWN", blurb: "for people figuring out what comes next.", x: 88, y: 74 },
];

export const boatMapCopy = {
  title: "The harbour, after dark.",
  sub: "Every boat out there is a problem someone is working through this week. Names change. The water doesn't.",
  crewLine: "a few people currently rowing",
  crewNote: "real counts arrive when the community does. we don't invent numbers.",
};

/* ------------------------------------------------------------------ */
/* Interaction 03 · WHO'S IN THE SAME BOAT? (fictional placeholders)   */
/* ------------------------------------------------------------------ */

export const builderNotes: string[] = [
  "I'm building a devtool. Trying to land my first 10 customers.",
  "I'm shipping AI agents into production. Still figuring out what to trust.",
  "Have product-market fit. Now trying to build a sales motion.",
  "I'm raising my first round. No idea what I'm doing.",
  "Quit my job three weeks ago. The idea is good. Probably.",
  "Rebuilt onboarding four times. Users still get lost. Send help.",
  "Two founders, one laptop charger. Somehow shipping weekly.",
];

export const notesCopy = {
  title: "Who's in the same boat?",
  another: "Another one",
  outro: "Somewhere out there, someone is probably dealing with the same thing.",
  cta: "Find your boat",
};

/* ------------------------------------------------------------------ */
/* Interaction 04 · SHOW US THE THING (the messy wall)                 */
/* ------------------------------------------------------------------ */

export type WallItem =
  | { kind: "sticky"; text: string; rotate: number }
  | { kind: "snippet"; title: string; code: string; rotate: number }
  | { kind: "diagram"; rotate: number }
  | { kind: "idea"; title: string; text: string; rotate: number }
  | { kind: "mystery"; rotate: number };

export const wallItems: WallItem[] = [
  {
    kind: "idea",
    title: "side project, weekends only",
    text: "a tiny CRM for people who hate CRMs. three users. one is my mum.",
    rotate: -2,
  },
  {
    kind: "snippet",
    title: "agent.ts",
    code: `const reply = await agent.run(task);
if (!reply.ok) {
  // the 10th time is a story
  return fallback(task);
}`,
    rotate: 1.5,
  },
  {
    kind: "sticky",
    text: "demo day: the demo finally worked. nobody saw. doing it again.",
    rotate: 3,
  },
  { kind: "diagram", rotate: -1 },
  {
    kind: "idea",
    title: "prototype no. 4",
    text: "voice notes in, structured specs out. unfinished. promising. loud.",
    rotate: 2,
  },
  {
    kind: "sticky",
    text: "note to self: stop rebuilding the landing page. ship the product.",
    rotate: -3,
  },
  { kind: "mystery", rotate: 1 },
];

export const wallCopy = {
  title: "Show us the thing.",
  sub: "A title doesn't tell us much. Show us what you actually do.",
  cta: "I have something to show",
  mystery: "WE DON'T KNOW WHAT THIS ONE IS YET.",
  dragHint: "go on, move things around. it's someone's desk, not a museum.",
};

/* ------------------------------------------------------------------ */
/* Interaction 05 · THE NO-PITCHES TABLE                               */
/* ------------------------------------------------------------------ */

export const chairPrompts: string[] = [
  "what are you trying to solve?",
  "what broke this week?",
  "what do you need?",
  "what have you figured out?",
  "what can you help with?",
  "what are you avoiding?",
];

export const tableCopy = {
  titleLines: ["No stage.", "No pitch deck.", "Just a table."],
  sub: "Small groups. Real problems. People actually doing the work.",
  note: "please don't pitch anyone.",
};

/* ------------------------------------------------------------------ */
/* Interaction 06 · ONE ASK                                            */
/* ------------------------------------------------------------------ */

export const asks: string[] = [
  "I need someone who's sold enterprise software.",
  "I need a designer who understands developer tools.",
  "I need someone who's raised a pre-seed round.",
  "I need someone who's actually deployed agents.",
  "I need my first 10 customers.",
];

export const oneAskCopy = {
  title: "One ask.",
  sub: "Everyone arrives with one thing they need. Someone in the room usually has it.",
  cardLabel: "ONE ASK",
  cta: "I have an ask",
};

/* ------------------------------------------------------------------ */
/* The letter, philosophy, audience, curation, CTAs                    */
/* ------------------------------------------------------------------ */

export const letterCopy = {
  paragraphs: [
    "We've spent the last few years building in tech. Somewhere along the way we realised the people who helped us most weren't the people we were supposed to meet.",
    "They were other builders. People who had already broken the thing we were about to break. People who knew the customer we needed. People who could answer a question without turning it into a pitch.",
    "Now we're building Autter, surrounded by a whole new generation figuring out how to build and ship with AI.",
    "So we made the room we wanted to have.",
  ],
  signoff: "Sagnik + Tanvi",
  signoffNote: "founders of Autter, hosts of Same Boat",
};

/* The landing note: shown before the site, then dismissed for the session. */
export const letterGateCopy = {
  kicker: "a note from the keepers",
  salutation: "to anyone building with AI,",
  seal: "SAME BOAT",
  enter: "Come in and see the whole thing",
  hint: "no sign-up yet. just go have a look.",
};

export const whyLetterContent = {
  hook: {
    kicker: "The keeper's log · personal correspondence",
    title: "Why this exists.",
    intro: [
      "i'm sagnik. one night, while tanvi and i were building autter, i sent her a thought:",
      "we should get a few people who ship with ai agents into a room. small tables. everyone opens their real workflow, shows what broke, and asks for the help they need.",
    ],
  },
  paragraphs: [
    "the thought came from a problem we kept running into.",
    "everyone was talking about agents. we could find endless demos, predictions and tool launches. we could not find many people talking about the work itself.",
    "what do you still review line by line? which tasks do you trust an agent to handle? where does it save you a day? where does it create a mess? how do you give it context? when do you take over?",
    "tanvi and i were working through these questions while building autter. the useful answers came from other builders, often through a private message or a late-night call. someone would share a workflow, explain a failure or make an introduction without asking for anything in return.",
    "i had seen this pattern before.",
    "i've founded two companies and sold one. after the exit, i spent years as a startup scout, trying to find strong founders before everyone else noticed them.",
    "i also joined the invite-only founder circles that promised access to a remarkable network. many became quiet group chats filled with introductions and little else.",
    "the people who helped me build were other builders. they replied when something broke. they shared what they had learned. they opened doors because they understood the problem.",
    "so we're building a room around that behaviour.",
    "we'll bring together tables of six to eight people, matched around the problems they are solving. everyone at the table will be shipping software with agents.",
    "on each call, one person will open their stack and walk through the real workflow. the group will see what works, what still needs human judgment and what has failed.",
    "then each person will say what they need.",
    "maybe you are struggling to make an agent reliable across a large codebase. maybe you want feedback on how you review generated work. maybe you need an introduction to someone who has solved the same problem.",
    "the table can help while the context is fresh.",
    "you get a small group of peers who understand the work because they do it too. you see workflows that people rarely share in public. you learn where experienced builders place checks, what they trust and which approaches they have dropped.",
    "you can bring a problem while it is still costing you time. someone at the table may have dealt with it last week.",
    "a demo gets you a seat. founders, staff engineers, indie hackers and agency leads can all apply. your work matters more than your title, funding stage or follower count. come ready to show what you are building and talk about the parts that still hurt.",
    "we're starting in pune and mumbai.",
    "tanvi and i will read every application ourselves. seats will stay limited because the value comes from everyone having enough space to contribute, ask and be remembered.",
    "if you're shipping software with ai agents in either city and want a few good people to compare notes with, apply here.",
  ],
  signoff: "sagnik & tanvi",
  signoffNote: "building autter",
};

export const principles: { title: string; line: string }[] = [
  { title: "Show, don't tell.", line: "A demo beats a title." },
  { title: "Bring the real problem.", line: "Not the LinkedIn version." },
  { title: "Give before you take.", line: "Make the room better." },
  { title: "No spectators.", line: "Everyone brings something." },
  { title: "Keep it small.", line: "The point is to know the people in the room." },
];

export const whoForCopy = {
  yesTitle: "You might be in the right place if...",
  yes: [
    "you're shipping software",
    "you're starting a company",
    "you're building with AI",
    "you're chasing your first customers",
    "you're figuring out sales",
    "you're hiring",
    "you're raising",
    "you're launching something",
    "you're deciding what's next",
    "you have something real to show",
  ],
  noTitle: "You probably shouldn't be here if...",
  no: [
    "you just want another LinkedIn group",
    "you want an audience",
    "you want to pitch everyone",
  ],
  asides: [
    "yes, you can bring your weird side project.",
    "no, follower count doesn't matter.",
  ],
};

export const curatedCopy = {
  title: "We're keeping this small on purpose.",
  body: [
    "Not because we're trying to make a club.",
    "Because once there are too many people in the room, people stop talking to each other.",
    "So we read applications ourselves and figure out who belongs together.",
  ],
  cta: "Tell us about yourself",
};

export const finalCtaCopy = {
  titleLines: ["Fill this form.", "We'll worry about", "the boat later."],
  cta: "Apply to Same Boat",
  note: "no promises. except the boat.",
};

export const footerCopy = {
  statement: "Different boats. Same water.",
  meta: "Same Boat is a community by Autter, hosted by Sagnik and Tanvi.",
  log: "CAPTAIN'S LOG, DAY 001: still trying to figure out how to make people talk to strangers.",
  sos: {
    label: "SOS",
    title: "What's broken?",
    body: "Tell us the thing you're stuck on. Someone here has probably broken it before you.",
    cta: "Send a flare",
  },
};

export const heroCopy = {
  kicker: "a little voyage by Autter",
  sub: "We're all building different things. Sometimes we're in exactly the same boat. Six stops. One harbour.",
  primaryCta: "Set sail",
  secondaryCta: "skip the voyage, go to the form",
};

/* ------------------------------------------------------------------ */
/* The voyage (game layer)                                             */
/* ------------------------------------------------------------------ */

export const gameCopy = {
  waypointLabel: "waypoint",
  logged: "logged in the captain's log.",
  hud: {
    logLabel: "captain's log",
    crewLabel: "crew",
    apply: "apply",
    reset: "abandon voyage",
  },
  stations: {
    boat: {
      hook: "Every voyage starts with admitting which boat you're actually in.",
    },
    crew: {
      title: "Muster your crew.",
      sub: "Boats are better with a crew. Bring the friends who are building too.",
      label: "who's coming aboard?",
      placeholder: "a friend who builds",
      add: "Add to crew",
      remove: "Remove",
      empty: "just the captain so far.",
      solo: "sailing solo? also fine.",
      soloButton: "I'm sailing solo",
      full: "that's a full boat.",
      note: "One application each. We'll take names for now; the form asks the rest.",
      aboard: "aboard:",
    },
    water: {
      hook: "Sail close to another boat and see what they're rowing at.",
    },
    notes: {
      hook: "Driftwood floats past. Every piece has a note on it.",
    },
    table: {
      hook: "Drop anchor. Take a seat. Say the real thing.",
    },
    wall: {
      hook: "The harbour wall, where people pin what they're actually making.",
    },
    asks: {
      hook: "The buoy where everyone ties one ask before sailing on.",
    },
  },
  arrival: {
    place: "Same Boat Harbour",
    vessel: "vessel",
    crew: "crew",
    ports: "ports",
    tbd: "TBD",
    solo: "just you",
    incompleteVoyage: "you skipped some water. the boat floats anyway.",
    completeVoyage: "full voyage. the harbourmaster is impressed.",
  },
};

export const manifestoCopy = {
  lineOne: "You don't have to be building the same thing.",
  lineTwo: "You just have to be trying to solve the same problem.",
  small: "Different companies. Different backgrounds. Sometimes, the exact same week.",
};

/* ------------------------------------------------------------------ */
/* The Harbour (the playable game)                                     */
/* ------------------------------------------------------------------ */

export const playCopy = {
  title: "Same Boat",
  tagline: "The harbour lights when builders find one another.",
  intro:
    "Same Boat Harbour's beacon is fading. Its light is made from what builders give one another: clarity, company, and lessons earned the hard way. Take the orange sail, recover all three, and bring the light home.",
  start: "Set sail",
  resume: "Back to the water",
  skip: "skip the voyage, go to the form",
  readPage: "read it as a page instead",
  objective: "Mission: name your challenge, rescue one builder, recover three harbour rules, then follow the compass north and relight the harbour.",
  controlsDesktop: "Arrow keys or WASD to sail. Hold and drag the water too. E to dock.",
  controlsTouch: "Hold and drag the water to sail. Tap the button to dock.",
  story: {
    label: "captain's log",
    opening:
      "Same Boat Harbour had gone quiet. You left the boatyard with one orange sail and a question: who else is crossing the same water?",
    resumed: "Your wake is still on the chart. The harbour is waiting for the rest of the story.",
    port: (label: string) =>
      `The ${label} boat answered your hail. Different work, familiar water — another builder carrying a problem like yours.`,
    claimed: (label: string) =>
      `You named the boat you are in: ${label}. A problem becomes easier to navigate once it has a name.`,
    crew: (name: string) =>
      `${name} climbed aboard. The boat sat lower in the water, but the crossing already felt lighter.`,
    bottle:
      "A bottle knocked against the hull. Someone you have never met had left proof that your hard week was not yours alone.",
    lighthouse:
      "The keeper's log held the oldest harbour lesson: people do not need the same destination to help each other cross.",
    fleet:
      "Another builder passed close enough to wave. Every moving light on the water was a problem in progress.",
    landmark: (name: string) =>
      `You went ashore at ${name}. Here, unfinished work was welcome — and honest questions bought a seat at the table.`,
    rule: (index: number) =>
      `Harbour rule ${index} entered the log. The light stays on because every crew leaves something useful behind.`,
    whale:
      "The oldest listener surfaced beside you. For a moment, the harbour became quiet enough for the truth to be heard.",
    ready:
      "Three lights are aboard: a named problem, someone beside you, and rules worth sharing. Follow the compass north and bring them home.",
    harbourLocked: (objective: string) =>
      `The harbour light is still too faint. One part of the story is missing: ${objective.toLowerCase()}`,
  },
  mission: {
    label: "your mission",
    title: "Relight the harbour",
    premise: "The beacon burns on three things builders give one another: clarity, company, and hard-won knowledge.",
    vessel: "Name the problem you are carrying",
    crew: "Bring one stranded builder aboard",
    rules: "Recover three harbour rules",
    return: "Follow the compass north to Same Boat Harbour",
    ready: "All three lights recovered",
    lockedAction: "See what is missing",
  },
  hud: { ports: "boats", crew: "crew", vessel: "vessel", apply: "apply", none: "none yet" },
  action: {
    port: "Dock here",
    crew: "Bring aboard",
    bottle: "Read it",
    lighthouse: "Read the log",
    harbour: "Make port",
    fleet: "Hail them",
    landmark: "Go ashore",
    rule: "Log the rule",
    whale: "Say hello",
  },
  port: {
    visiting: "just visiting",
    claim: "that's my boat",
    claimed: "this is your boat.",
    already: "you already claimed a boat. switch to this one?",
    switch: "switch to this boat",
  },
  crew: {
    title: "Someone's waving from a raft.",
    prompt: "who's coming aboard?",
    placeholder: "a friend who builds",
    add: "Haul them in",
    full: "That's a full boat. Four's plenty.",
    note: "One application each. We'll take a name for now; the form asks the rest.",
  },
  bottle: { label: "Message in a bottle", from: "washed up from someone in the same boat" },
  log: { label: "The keeper's log", sign: "found nailed to the lighthouse door" },
  arrived: {
    title: "The harbour is brighter.",
    sub: "You came back carrying more than you left with. Here is what your voyage added to the harbour.",
    vessel: "vessel",
    crew: "crew",
    boatsSeen: "boats visited",
    solo: "just you",
    full: "You named the problem, heard the harbour, and brought its whole code home.",
    some: "You found part of your story out there. The rest is easier with people beside you.",
    none: "You sailed straight in. Sometimes asking for a room is the first brave thing.",
    closing: "The harbour does not promise calm water. It gives you people who know how to cross it.",
    apply: "Apply to Same Boat",
    keepSailing: "keep sailing",
    note: "Different work. Shared water.",
  },
  reset: "scuttle this voyage",
};

/* Things to find out on the water: passing boats, landmark islands,
 * collectible harbour rules, and one shy whale. */
export const seaCopy = {
  fleet: {
    label: "another boat",
    ask: "They are carrying a problem you might recognise. Hail now. Help later.",
    close: "sail on",
  },
  landmarks: {
    table: {
      name: "Table Island",
      titleLines: ["Pull up a chair.", "Leave the pitch aboard."],
      sub: "The harbour started here: six builders, one table, and honest questions nobody could answer alone.",
      prompts: chairPrompts.slice(0, 4),
      note: "the only price of a seat is a real question.",
    },
    workshop: {
      name: "The Workshop",
      title: "Bring the unfinished thing.",
      sub: "Half-built ideas are repaired here in public. Nobody asks for polish. They ask where it hurts.",
      note: "leave with one useful next move, not ten opinions.",
    },
    noticewall: {
      name: "The Notice Wall",
      title: "Leave one ask. Take one answer.",
      sub: "Every card came from someone willing to be specific. Every empty pin is an invitation to help.",
      label: oneAskCopy.cardLabel,
      asks: asks.slice(0, 3),
    },
  },
  rules: {
    label: "harbour rule",
    tally: "harbour rules",
    of: "of",
    hint: "five rules keep the harbour light burning. each one was learned the difficult way.",
  },
  whale: {
    label: "something big",
    title: "The oldest listener in the harbour.",
    lines: [
      "She surfaces when the water gets quiet enough for someone else to finish speaking.",
      "The harbourmaster says that is why she has heard every good idea twice: once as a pitch, and once as the truth.",
    ],
    close: "let her be",
  },
};

/* ------------------------------------------------------------------ */
/* The Notice Wall (live community board in the harbour game)          */
/* ------------------------------------------------------------------ */

export const noticeWallCopy = {
  label: "the notice wall",
  boardNote: "every card came from someone crossing this water.",
  founding: "pinned by the founding crew",
  loading: "reading the wall...",
  offline: "the live wall is out of reach right now. the founding cards below still hold.",
  form: {
    title: "Pin your ask",
    placeholder: "I need someone who's...",
    signedLabel: "signed",
    signedPlaceholder: "your name, or your boat",
    submit: "Pin it to the wall",
    sending: "the harbourmaster is reading your card...",
    note: "one specific ask beats three vague ones. the harbourmaster reads every card before it goes up: keep it about the work.",
    maxLength: 140,
  },
  moderation: {
    pinned: "Pinned. Your card is on the wall.",
    rejectedLabel: "handed back by the harbourmaster",
    unavailable: "The harbourmaster has stepped away from the wall. Try pinning your card again in a moment.",
  },
  close: "Back to the water",
};

/* Captain Patch easter egg lines: click him repeatedly. */
export const patchLines: string[] = [
  "hello. I'm Patch. I mostly supervise.",
  "still here.",
  "the water's fine. the market, less so.",
  "have you tried talking to a customer today?",
  "mate, you're really clicking this thing.",
];
